import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, Building2, Globe, AlertCircle, Phone } from 'lucide-react';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export function StartHereForm() {
  console.log('✅ StartHereForm component mounted');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    websiteUrl: '',
    servicesInterested: [] as string[],
    businessDescription: '',
  });

  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage('');
  };

  const handleCheckboxChange = (interest: string) => {
    const newInterests = formData.servicesInterested.includes(interest)
      ? formData.servicesInterested.filter(i => i !== interest)
      : [...formData.servicesInterested, interest];
    setFormData({ ...formData, servicesInterested: newInterests });
    if (errorMessage) setErrorMessage('');
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name.');
      return false;
    }
    if (!formData.businessName.trim()) {
      setErrorMessage('Please enter your business name.');
      return false;
    }
    if (!formData.email.trim()) {
      setErrorMessage('Please enter your email address.');
      return false;
    }
    if (!validateEmail(formData.email)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }
    if (!formData.businessDescription.trim()) {
      setErrorMessage('Please describe your business and main service.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Form submission started');
    setErrorMessage('');

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    console.log('✅ Form validation passed');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    console.log('🔑 Supabase URL exists:', !!supabaseUrl);
    console.log('🔑 Supabase Key exists:', !!supabaseAnonKey);

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('❌ Supabase configuration missing');
      setErrorMessage('Submission is temporarily unavailable. Please email me directly.');
      setStatus('error');
      return;
    }

    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/submit-form`;
    console.log('📡 Submitting to edge function:', edgeFunctionUrl);
    setStatus('submitting');

    try {
      const payload = {
        name: formData.name,
        businessName: formData.businessName,
        email: formData.email,
        phone: formData.phone,
        websiteUrl: formData.websiteUrl,
        servicesInterested: formData.servicesInterested,
        businessDescription: formData.businessDescription,
        source: 'Website',
      };

      console.log('📦 Payload:', payload);

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify(payload),
      });

      console.log('📨 Response status:', response.status);
      console.log('📨 Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Response error:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('✅ Form submitted successfully');

      // Track Meta Pixel Lead event
      if (typeof window !== 'undefined' && (window as any).fbq) {
        console.log('🎯 Firing Meta Pixel Lead event from StartHereForm');
        (window as any).fbq('track', 'Lead', {
          content_name: 'Start Here Form',
          content_category: 'lead_generation',
          value: formData.servicesInterested.length,
          currency: 'USD'
        });
        console.log('✅ Meta Pixel Lead event fired successfully');
      } else {
        console.log('⚠️ Meta Pixel (fbq) not available on StartHereForm submission');
      }

      console.log('📍 Navigating to thank you page');

      // Delay navigation slightly to ensure pixel fires
      setTimeout(() => {
        navigate('/thank-you');
      }, 300);
    } catch (error) {
      console.error('❌ Form submission error details:', error);
      console.error('❌ Error name:', error instanceof Error ? error.name : 'Unknown');
      console.error('❌ Error message:', error instanceof Error ? error.message : 'Unknown');

      setStatus('error');
      setErrorMessage('Something went wrong sending your details. Please try again in a minute or email me directly.');
    }
  };

  const interestOptions = [
    'Lead Rescue & Automation',
    'Social Media Marketing',
    'Website Design / Landing Page',
    'Chatbots & Phone Agent',
    'General Audit / Not Sure Yet',
  ];

  return (
    <div className="py-10 bg-black/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 id="start-here" className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ scrollMarginTop: '100px' }}>
            Ready to see what this could look like for your clinic?
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Fill out this quick form. I'll record a short Loom audit of your current setup and send you one clear recommendation: either a marketing fix or a lead-rescue fix tailored to your clinic.
          </p>
        </div>

        <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-blue-500/20">
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-md flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-200 text-sm">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-300 mb-2">
                  Business Name *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Your business name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-300 mb-2">
                  Website URL
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    id="websiteUrl"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="www.yourbusiness.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Which services are you interested in? (Select all that apply.)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {interestOptions.map((option) => (
                  <label key={option} className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex items-center justify-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={formData.servicesInterested.includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                        disabled={status === 'submitting'}
                        className="w-5 h-5 bg-gray-800/50 border border-gray-700 rounded cursor-pointer checked:bg-blue-500 checked:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      {formData.servicesInterested.includes(option) && (
                        <CheckCircle className="absolute w-5 h-5 text-white pointer-events-none" />
                      )}
                    </div>
                    <span className="text-gray-300 group-hover:text-white transition-colors text-sm">
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="businessDescription" className="block text-sm font-medium text-gray-300 mb-2">
                Briefly describe your business and main service *
              </label>
              <textarea
                id="businessDescription"
                name="businessDescription"
                value={formData.businessDescription}
                onChange={handleChange}
                disabled={status === 'submitting'}
                rows={4}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="E.g., We're a dental clinic with 3 locations. We get about 50-100 calls per day and struggle to follow up with missed calls..."
              />
            </div>

            <button
              type="submit"
              disabled={status === 'submitting'}
              onClick={() => console.log('🔵 Submit button clicked!')}
              className="w-full px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold rounded-md hover:from-green-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-green-500 disabled:hover:to-blue-500"
            >
              {status === 'submitting' ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
